import { CalendarPlus, FileCheck2, Plus, Trash2 } from "lucide-react";
import { ContextualNav } from "@/components/ContextualNav";
import { PaginationControls } from "@/components/PaginationControls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";

const COLORS = [
  { value: "blue", label: "Azul", swatch: "bg-blue-500" },
  { value: "emerald", label: "Verde", swatch: "bg-emerald-500" },
  { value: "amber", label: "Amarillo", swatch: "bg-amber-400" },
  { value: "violet", label: "Violeta", swatch: "bg-violet-500" },
  { value: "rose", label: "Rosa", swatch: "bg-rose-500" },
];

const CALENDAR_ACTIONS = [
  { to: "/p/material", icon: "material", label: "Crear material de estudio", description: "Sube páginas y genera ejercicios." },
  { to: "/p", icon: "dashboard", label: "Ver el panel", description: "Comprueba progreso y próximos exámenes." },
];

export function ParentCalendarContent(props) {
  return (
    <div className="space-y-8" data-testid="parent-calendar-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Materias y examenes</h1>
        <p className="mt-1 text-sm text-slate-500">Registra el calendario real de {props.student.first_name}.</p>
      </div>
      <SubjectsSection
        subjects={props.subjects}
        form={props.subjectForm}
        onFormChange={props.onSubjectFormChange}
        onSubmit={props.onAddSubject}
        onRemove={props.onRemoveSubject}
      />
      <ExamsSection
        subjects={props.subjects}
        exams={props.exams}
        form={props.examForm}
        onFormChange={props.onExamFormChange}
        onSubmit={props.onAddExam}
        scoreDraft={props.scoreDraft}
        onScoreDraftChange={props.onScoreDraftChange}
        onSaveScore={props.onSaveScore}
        onUploadCorrectedExam={props.onUploadCorrectedExam}
        onRemoveExam={props.onRemoveExam}
        offset={props.examOffset}
        total={props.examTotal}
        pageSize={props.examPageSize}
        onPageChange={props.onExamPageChange}
      />
      <ContextualNav title="Convierte las fechas en práctica" description="Con el calendario listo, prepara los apuntes del siguiente examen." items={CALENDAR_ACTIONS} />
    </div>
  );
}

function SubjectsSection({ subjects, form, onFormChange, onSubmit, onRemove }) {
  const selectedColor = COLORS.find((color) => color.value === form.color) || COLORS[0];
  return (
    <section className="estudia-card p-5" data-testid="subjects-section">
      <h2 className="font-display text-xl font-bold text-slate-900">Materias</h2>
      <form onSubmit={onSubmit} className="mt-4 flex flex-wrap items-end gap-3" data-testid="subject-create-form">
        <div className="min-w-[200px] flex-1">
          <Label htmlFor="sname">Nombre</Label>
          <Input id="sname" required minLength={2} className="estudia-touch mt-1.5" placeholder="Ciencias Naturales" value={form.name} onChange={(event) => onFormChange({ ...form, name: event.target.value })} data-testid="subject-name-input" />
        </div>
        <SubjectColorSelect value={form.color} selectedColor={selectedColor} onChange={(color) => onFormChange({ ...form, color })} />
        <Button type="submit" className="estudia-touch rounded-xl bg-blue-600 font-semibold hover:bg-blue-700" data-testid="subject-create-submit-btn">
          <Plus className="mr-2 h-4 w-4" /> Agregar
        </Button>
      </form>
      {subjects.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">Sin materias todavia.</p>
      ) : (
        <ul className="mt-5 flex flex-wrap gap-2">
          {subjects.map((subject) => <SubjectChip key={subject.id} subject={subject} onRemove={onRemove} />)}
        </ul>
      )}
    </section>
  );
}

function SubjectColorSelect({ value, selectedColor, onChange }) {
  return (
    <div>
      <Label htmlFor="scolor">Color</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id="scolor" className="estudia-touch mt-1.5 w-24 rounded-xl border-slate-200 bg-white" aria-label={`Color seleccionado: ${selectedColor.label}`} data-testid="subject-color-select">
          <span className={`h-5 w-10 rounded-full border-2 border-white shadow-sm ring-1 ring-slate-200 ${selectedColor.swatch}`} aria-hidden="true" />
        </SelectTrigger>
        <SelectContent className="min-w-24 rounded-xl">
          {COLORS.map((color) => (
            <SelectItem key={color.value} value={color.value} aria-label={color.label} className="h-10 justify-center rounded-lg py-2 pl-2 pr-8">
              <span className={`block h-6 w-12 rounded-full border-2 border-white shadow-sm ring-1 ring-slate-200 ${color.swatch}`} aria-hidden="true" />
              <span className="sr-only">{color.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SubjectChip({ subject, onRemove }) {
  return (
    <li className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 pl-4 pr-2 py-1.5 text-sm font-semibold text-slate-700" data-testid="subject-chip">
      {subject.name}
      <button onClick={() => onRemove(subject.id)} className="grid h-7 w-7 place-items-center rounded-full text-slate-400 hover:bg-white" aria-label={`Eliminar ${subject.name}`} data-testid={`delete-subject-${subject.name}`}>
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

function ExamsSection({ subjects, exams, form, onFormChange, onSubmit, scoreDraft, onScoreDraftChange, onSaveScore, onUploadCorrectedExam, onRemoveExam, offset, total, pageSize, onPageChange }) {
  return (
    <section className="estudia-card p-5" data-testid="exams-section">
      <h2 className="font-display text-xl font-bold text-slate-900">Examenes</h2>
      <ExamForm subjects={subjects} form={form} onFormChange={onFormChange} onSubmit={onSubmit} />
      {exams.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">Sin examenes registrados.</p>
      ) : (
        <div className="mt-5 space-y-3">
          {exams.map((exam) => <ExamCard key={exam.id} exam={exam} draft={scoreDraft[exam.id] || {}} onDraftChange={(draft) => onScoreDraftChange({ ...scoreDraft, [exam.id]: draft })} onSave={() => onSaveScore(exam)} onUpload={() => onUploadCorrectedExam(exam)} onRemove={() => onRemoveExam(exam)} />)}
        </div>
      )}
      <PaginationControls offset={offset} limit={pageSize} total={total} onChange={onPageChange} label="examenes" />
    </section>
  );
}

function ExamForm({ subjects, form, onFormChange, onSubmit }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <form onSubmit={onSubmit} className="mt-4 grid gap-3 sm:grid-cols-4" data-testid="exam-create-form">
      <div className="sm:col-span-2">
        <Label htmlFor="etitle">Titulo</Label>
        <Input id="etitle" required className="estudia-touch mt-1.5" placeholder="Civilizaciones precolombinas" value={form.title} onChange={(event) => onFormChange({ ...form, title: event.target.value })} data-testid="exam-title-input" />
      </div>
      <div>
        <Label htmlFor="esubject">Materia</Label>
        <select id="esubject" required className="estudia-touch mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" value={form.subject_id} onChange={(event) => onFormChange({ ...form, subject_id: event.target.value })} data-testid="exam-subject-select">
          <option value="">Selecciona...</option>
          {subjects.map((subject) => <option key={subject.id} value={subject.id}>{subject.name}</option>)}
        </select>
      </div>
      <div>
        <Label htmlFor="edate">Fecha</Label>
        <Input id="edate" type="date" required min={today} className="estudia-touch mt-1.5" value={form.exam_date} onChange={(event) => onFormChange({ ...form, exam_date: event.target.value })} data-testid="exam-date-input" />
      </div>
      <div className="sm:col-span-4">
        <Button type="submit" disabled={subjects.length === 0} className="estudia-touch rounded-xl bg-blue-600 font-semibold hover:bg-blue-700" data-testid="exam-create-submit-btn">
          <CalendarPlus className="mr-2 h-4 w-4" /> Registrar examen
        </Button>
      </div>
    </form>
  );
}

function ExamCard({ exam, draft, onDraftChange, onSave, onUpload, onRemove }) {
  const days = Math.ceil((new Date(exam.exam_date) - new Date()) / 86400000);
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="exam-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="font-display font-bold text-slate-900">{exam.title}</p><p className="text-sm text-slate-500">{exam.subject} · {exam.exam_date}</p></div>
        <div className="flex items-center gap-2">
          <ExamStatus exam={exam} days={days} />
          <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="h-10 w-10 rounded-xl text-rose-600 hover:bg-rose-50 hover:text-rose-700" aria-label={`Eliminar examen ${exam.title}`} data-testid={`delete-exam-${exam.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        {exam.real_score !== null ? (
          <p className="text-sm font-semibold text-emerald-700" data-testid="exam-real-score">Nota real: {exam.real_score}{exam.real_score_max ? ` / ${exam.real_score_max}` : ""}</p>
        ) : (
          <ScoreEditor examId={exam.id} draft={draft} onChange={onDraftChange} onSave={onSave} />
        )}
      </div>
      {exam.real_score === null && (
        <div className="mt-4 rounded-2xl bg-gradient-to-r from-violet-50 to-cyan-50 p-4">
          <p className="font-display text-base font-extrabold text-slate-900">Verificar examen corregido</p>
          <p className="mt-1 text-xs font-semibold text-slate-500">La imagen o PDF permite validar la nota, evitar duplicados y calcular XP de nota y mejora.</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Input type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" className="estudia-touch max-w-sm bg-white" onChange={(event) => onDraftChange({ ...draft, file: event.target.files?.[0] || null })} data-testid={`exam-proof-${exam.id}`} />
            <Button className="estudia-touch rounded-xl bg-violet-600 font-extrabold hover:bg-violet-500" disabled={!draft.file || draft.score === undefined || draft.score === ""} onClick={onUpload} data-testid={`exam-proof-submit-${exam.id}`}><FileCheck2 className="mr-2 h-4 w-4" /> Verificar y calcular XP</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ExamStatus({ exam, days }) {
  const tone = exam.status === "done" ? "bg-slate-100 text-slate-600" : days <= 3 ? "bg-rose-50 text-rose-700" : "bg-blue-50 text-blue-700";
  const label = exam.status === "done" ? "Realizado" : days < 0 ? "Pasado" : `Faltan ${days} dias`;
  return <span className={`rounded-full px-3 py-1 text-xs font-bold ${tone}`}>{label}</span>;
}

function ScoreEditor({ examId, draft, onChange, onSave }) {
  return (
    <>
      <Input placeholder="Nota" className="estudia-touch w-24" value={draft.score ?? ""} onChange={(event) => onChange({ ...draft, score: event.target.value })} data-testid={`exam-score-input-${examId}`} />
      <Input placeholder="Sobre" className="estudia-touch w-24" value={draft.max ?? ""} onChange={(event) => onChange({ ...draft, max: event.target.value })} data-testid={`exam-score-max-input-${examId}`} />
      <Button variant="outline" className="estudia-touch rounded-xl" onClick={onSave} data-testid={`exam-score-save-btn-${examId}`}>Guardar nota real</Button>
    </>
  );
}
